import styles from './ClientsList.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TitleSectionBack from '../../../Common/TitleSectionBack';
import {
  selectClientsShort,
  selectClientsListMeta,
  selectClientsListStats,
  matchesClientSearch,
  normalizeClientSearchForApi,
} from '../../store/selectors';
import { clientActions } from '../../store';
import ClientsTable from '../ClientsTable';
import clx from 'classnames';
import SearchInput from '../../../Common/SearchInput';
import { getActiveSalonId, getInvitiOnboarding, getInvitiOnboardingTot } from '../../../Auth/store/selectors';
import useWindowSize from '../../../../utils/useWindowSize';
import { Plus } from 'lucide-react';
import { modalActions } from 'Containers/Modals/store';
import { syncInviteJourney, shouldShowInvitePromo } from '../../utils/inviteJourney';
import InviteJourneyCard from '../../components/InviteJourneyCard';
import { useTranslation } from 'react-i18next';

const SKIP_LOADER = { skipLoader: true };
const PAGE_SIZE = 20;
const PAGINATION_DEBOUNCE_MS = 350;
const SEARCH_DEBOUNCE_MS = 450;

const FILTER_BY_ID = {
  0: 'all',
  1: 'active',
  2: 'non_active',
  3: 'disabled',
  4: 'from_salon',
  5: 'new',
};

const ClientsList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [activeFilterId, setActiveFilterId] = useState(0);
  const [searchedName, setSearchedName] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [paginationValue, setPaginationValueState] = useState({
    skip: 0,
    limit: PAGE_SIZE,
  });
  // Skip usato per la fetch: debounce solo sui click pagina a raffica
  const [fetchSkip, setFetchSkip] = useState(0);
  const hasLoadedOnceRef = useRef(false);
  const searchDebounceRef = useRef(null);
  const prevDebouncedSearchRef = useRef('');
  const pendingSearchResetRef = useRef(false);
  const lastStatsQueryRef = useRef('');
  const { isMobile } = useWindowSize();
  const activeSalonId = useSelector(getActiveSalonId());
  const clients = useSelector(selectClientsShort);
  const displayedClients = useMemo(() => {
    const query = debouncedSearch.trim();
    if (!query || !/\s/.test(query)) return clients;
    return clients.filter((client) => matchesClientSearch(client, query));
  }, [clients, debouncedSearch]);
  const listMeta = useSelector(selectClientsListMeta);
  const listStats = useSelector(selectClientsListStats);
  const userInvited = useSelector(getInvitiOnboarding()) ?? 0;
  const inviteTotal = useSelector(getInvitiOnboardingTot());

  const setPaginationValue = useCallback((next) => {
    setPaginationValueState((prev) => {
      const value = typeof next === 'function' ? next(prev) : next;
      if (!value) return prev;
      if (prev.skip === value.skip && prev.limit === value.limit) return prev;
      return value;
    });
  }, []);

  const resetToFirstPage = useCallback(() => {
    setPaginationValueState((prev) => (prev.skip === 0 ? prev : { ...prev, skip: 0 }));
    setFetchSkip(0);
  }, []);

  useEffect(() => {
    hasLoadedOnceRef.current = false;
    prevDebouncedSearchRef.current = '';
    pendingSearchResetRef.current = false;
    lastStatsQueryRef.current = '';
    setDebouncedSearch('');
    setSearchedName('');
    setInitialLoading(true);
  }, [activeSalonId]);

  const flushDebouncedSearch = useCallback((value) => {
    if (searchDebounceRef.current) {
      window.clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    setDebouncedSearch(value);
  }, []);

  const handleSearchChange = useCallback(
    (value) => {
      setSearchedName(value);
      if (!value.trim()) {
        flushDebouncedSearch('');
        return;
      }
      if (searchDebounceRef.current) {
        window.clearTimeout(searchDebounceRef.current);
      }
      searchDebounceRef.current = window.setTimeout(() => {
        searchDebounceRef.current = null;
        setDebouncedSearch(value);
      }, SEARCH_DEBOUNCE_MS);
    },
    [flushDebouncedSearch],
  );

  const handleSearchKeyDown = useCallback(
    (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      flushDebouncedSearch(searchedName);
    },
    [flushDebouncedSearch, searchedName],
  );

  useEffect(
    () => () => {
      if (searchDebounceRef.current) {
        window.clearTimeout(searchDebounceRef.current);
      }
    },
    [],
  );

  // Debounce solo il cambio pagina (click a raffica sulla paginazione)
  useEffect(() => {
    if (paginationValue.skip === fetchSkip) return undefined;
    const timer = window.setTimeout(() => {
      setFetchSkip(paginationValue.skip);
    }, PAGINATION_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [paginationValue.skip, fetchSkip]);

  // Reset pagina su cambio search (prima della fetch, evita doppia request)
  useEffect(() => {
    if (prevDebouncedSearchRef.current === debouncedSearch) return;
    prevDebouncedSearchRef.current = debouncedSearch;
    pendingSearchResetRef.current = true;
    resetToFirstPage();
  }, [debouncedSearch, resetToFirstPage]);

  const onFilterChange = useCallback(
    (filterId) => {
      setActiveFilterId(filterId);
      resetToFirstPage();
    },
    [resetToFirstPage],
  );

  useEffect(() => {
    if (!activeSalonId) return undefined;

    if (pendingSearchResetRef.current && fetchSkip !== 0) {
      return undefined;
    }
    pendingSearchResetRef.current = false;

    const search = debouncedSearch.trim();
    const apiSearch = normalizeClientSearchForApi(search);
    let cancelled = false;
    const soft = hasLoadedOnceRef.current;
    if (soft) setRefreshing(true);
    else setInitialLoading(true);

    const page = Math.floor(fetchSkip / paginationValue.limit) + 1;
    const filter = FILTER_BY_ID[activeFilterId] || 'all';
    const listPayload = {
      page,
      limit: paginationValue.limit,
      search: apiSearch,
      filter,
    };

    dispatch(
      clientActions.GET_CLIENTS_PLUS.REQUEST(
        listPayload,
        () => {
          if (cancelled) return;
          hasLoadedOnceRef.current = true;
          setInitialLoading(false);
          setRefreshing(false);
        },
        SKIP_LOADER,
      ),
    );

    const statsQueryKey = `${activeSalonId}|${search}`;
    if (lastStatsQueryRef.current !== statsQueryKey) {
      lastStatsQueryRef.current = statsQueryKey;
      dispatch(
        clientActions.GET_CLIENTS_STATS.REQUEST({ filter: 'all', search: apiSearch }, null, SKIP_LOADER),
      );
    }

    return () => {
      cancelled = true;
    };
  }, [
    dispatch,
    activeSalonId,
    activeFilterId,
    debouncedSearch,
    fetchSkip,
    paginationValue.limit,
  ]);

  const inviteJourney = useMemo(
    () => syncInviteJourney(activeSalonId, userInvited, inviteTotal),
    [activeSalonId, userInvited, inviteTotal],
  );
  const showInviteCard = shouldShowInvitePromo(inviteJourney);
  const pageCount =
    listMeta.totalPages ||
    Math.ceil((listMeta.total || 0) / (paginationValue.limit || PAGE_SIZE)) ||
    1;
  const openCreateClient = () => {
    dispatch(
      modalActions.OPEN_MODAL.SUCCESS({
        type: 'createClientModal',
        props: { idsalone: activeSalonId },
      }),
    );
  };

  return (
    <>
      <div
        className={clx(styles.clients_list_container, {
          [styles.clients_list_container_mobile]: isMobile,
        })}
      >
        <div className={styles.header}>
          <TitleSectionBack
            title={t('clients.list.title')}
            description={t('clients.list.description')}
          />
          {showInviteCard ? (
            <div className={styles.invite_slot}>
              <InviteJourneyCard journey={inviteJourney} />
            </div>
          ) : null}
          <button type="button" className={styles.add_client_btn} onClick={openCreateClient}>
            <Plus size={isMobile ? 18 : 16} strokeWidth={2.4} />
            <span>{t('clients.list.addClient')}</span>
          </button>
        </div>
        <div className={styles.filters_container}>
          <div className={styles.search_wrap}>
            <SearchInput
              placeholder={t('clients.list.searchPlaceholder')}
              value={searchedName}
              setValue={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              backgroundColor={'white'}
            />
          </div>
        </div>
        <ClientsTable
          values={displayedClients}
          clientsStats={listStats}
          isMobile={isMobile}
          activeFilterId={activeFilterId}
          setActiveFilterId={onFilterChange}
          initialLoading={initialLoading}
          refreshing={refreshing}
          serverSidePagination
          setPaginationValue={setPaginationValue}
          pageCount={pageCount}
          pageRows={PAGE_SIZE}
          entityId={`${activeSalonId}-${activeFilterId}-${debouncedSearch}`}
        />
      </div>
    </>
  );
};

export default ClientsList;
